import React, { useEffect, useState, useCallback } from 'react';
import { Eyebrow, PageHeading } from '../../../../design-system';
import { fetchContactMessages, type ContactMessage } from '../../../../api/billingApi';
import {
  adminGetTicket, adminListTickets, adminReply, adminUpdate, LIFECYCLE_LABEL,
  type Lifecycle, type Priority, type Ticket,
} from '../../../../api/supportApi';
import { LifecycleBadge, PriorityBadge, TicketHistory, fmtWhen } from '../../../support/TicketThread';
import styles from './MessagesSection.module.css';

/**
 * Support inbox (SRS #17/#18): every ticket with its lifecycle, priority and
 * assignee; reply, move, assign, and read the full history.
 */

interface MessagesSectionProps {
  token: string;
}

const FILTERS: { key: string; label: string }[] = [
  { key: 'active', label: 'Active' }, { key: 'open', label: 'Open' }, { key: 'in_progress', label: 'In progress' },
  { key: 'waiting_on_customer', label: 'Waiting on customer' }, { key: 'resolved', label: 'Resolved' }, { key: 'closed', label: 'Closed' }, { key: '', label: 'All' },
];
const NEXT: Record<Lifecycle, Lifecycle[]> = {
  open: ['in_progress', 'resolved', 'closed'],
  in_progress: ['open', 'resolved', 'closed'],
  waiting_on_customer: ['in_progress', 'resolved', 'closed'],
  resolved: ['open', 'closed'],
  closed: ['open'],
};

export const MessagesSection: React.FC<MessagesSectionProps> = ({ token }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [counts, setCounts] = useState<Partial<Record<Lifecycle, number>>>({});
  const [filter, setFilter] = useState('active');
  const [mine, setMine] = useState(false);
  const [open, setOpen] = useState<Ticket | null>(null);
  const [attachments, setAttachments] = useState<Record<number, string | null>>({});
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await adminListTickets({ lifecycle: filter, assigned: mine ? 'me' : undefined, limit: 200 });
      setTickets(r.tickets); setCounts(r.counts);
      // Attachment links still come from the legacy inbox endpoint (presigned URLs).
      fetchContactMessages(token).then((legacy: ContactMessage[]) => {
        const map: Record<number, string | null> = {};
        legacy.forEach((m) => { map[m.id] = m.attachment_url ?? null; });
        setAttachments(map);
      }).catch(() => {});
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load tickets'); }
    finally { setLoading(false); }
  }, [filter, mine, token]);
  useEffect(() => { load(); }, [load]);

  const show = async (id: number) => {
    try { setOpen(await adminGetTicket(id)); setDraft(''); } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load ticket'); }
  };
  const act = async (fn: () => Promise<Ticket>) => {
    setBusy(true); setError(null);
    try { const t = await fn(); setOpen(t); setDraft(''); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Something went wrong'); }
    finally { setBusy(false); }
  };

  const active = (counts.open || 0) + (counts.in_progress || 0) + (counts.waiting_on_customer || 0);

  return (
    <section className={styles.shell}>
      <header className={styles.header}>
        <div>
          <Eyebrow tone="default" dot>SUPPORT</Eyebrow>
          <PageHeading accentWord="Tickets" accentTone="orange" size="h2" align="left" level="h2" className={styles.title}>
            Support Tickets
          </PageHeading>
          <p className={styles.subtitle}>
            {active} active · {counts.open || 0} open · {counts.in_progress || 0} in progress · {counts.waiting_on_customer || 0} waiting on customer · {counts.resolved || 0} resolved · {counts.closed || 0} closed
          </p>
        </div>
        <button className={styles.reloadBtn} onClick={load}>Refresh</button>
      </header>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '0 0 12px', alignItems: 'center' }}>
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                  style={{ padding: '5px 10px', borderRadius: 999, border: '1px solid var(--ds-border-strong)', background: filter === f.key ? 'var(--ds-brand-blue)' : 'var(--ds-surface)', color: filter === f.key ? 'var(--ds-text-on-dark)' : 'var(--ds-text-secondary)', cursor: 'pointer', fontSize: 12.5 }}>
            {f.label}
          </button>
        ))}
        <label style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--ds-text-muted)' }}><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> assigned to me</label>
      </div>

      {error && <p className={styles.errorText}>{error}</p>}
      {loading ? <p className={styles.loadingText}>Loading…</p> : tickets.length === 0 ? (
        <div className={styles.empty}><p>No tickets match this filter.</p></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: open ? 'minmax(min(280px, 100%), 1fr) 1.6fr' : '1fr', gap: 16 }}>
          <div className={styles.list}>
            {tickets.map((t) => (
              <div key={t.id} className={`${styles.card} ${t.read_state === 'unread' ? styles.cardUnread : ''}`} onClick={() => show(t.id)} style={{ cursor: 'pointer', borderColor: open?.id === t.id ? 'var(--ds-brand-blue)' : undefined }}>
                <div className={styles.cardTop}>
                  <div className={styles.cardLeft}>
                    <LifecycleBadge value={t.lifecycle} />
                    <PriorityBadge value={t.priority} />
                    <div>
                      <div className={styles.subject}>#{t.id} · {t.subject}</div>
                      <div className={styles.senderBlock}>
                        <span className={styles.senderName}>{t.user_name || t.user_email}</span>
                        {t.user_organization && <span className={styles.senderOrg}>{t.user_organization}</span>}
                      </div>
                    </div>
                  </div>
                  <div className={styles.cardRight}>
                    <span className={styles.date}>{fmtWhen(t.updated_at || t.created_at)}</span>
                    {t.assigned_to && <span style={{ fontSize: 11, color: 'var(--ds-text-muted)' }}>→ {t.assigned_to}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {open && (
            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 12, padding: 16, background: 'var(--ds-surface)', alignSelf: 'start' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16 }}>#{open.id} · {open.subject}</h3>
                  <div style={{ fontSize: 12, color: 'var(--ds-text-muted)', marginTop: 3 }}>
                    {open.user_name || ''} &lt;{open.user_email}&gt;{open.user_organization ? ` · ${open.user_organization}` : ''} · opened {fmtWhen(open.created_at)}
                    {attachments[open.id] && <> · <a href={attachments[open.id]!} target="_blank" rel="noreferrer">{open.attachment_name || 'attachment'}</a></>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><PriorityBadge value={open.priority} /><LifecycleBadge value={open.lifecycle} /></div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '12px 0', alignItems: 'center', fontSize: 12.5 }}>
                <select value="" disabled={busy} onChange={(e) => { const v = e.target.value as Lifecycle; if (v) act(() => adminUpdate(open.id, { lifecycle: v })); }}
                        style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--ds-border-strong)' }}>
                  <option value="">Move to…</option>
                  {NEXT[open.lifecycle].map((s) => <option key={s} value={s}>{LIFECYCLE_LABEL[s]}</option>)}
                </select>
                <select value={open.priority} disabled={busy} onChange={(e) => act(() => adminUpdate(open.id, { priority: e.target.value as Priority }))}
                        style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--ds-border-strong)' }}>
                  {(['low', 'normal', 'high', 'urgent'] as Priority[]).map((p) => <option key={p} value={p}>Priority: {p}</option>)}
                </select>
                {open.assigned_to
                  ? <span style={{ color: 'var(--ds-text-muted)' }}>Assigned to {open.assigned_to} <button type="button" disabled={busy} onClick={() => act(() => adminUpdate(open.id, { assign_to: '' }))} style={{ marginLeft: 4, cursor: 'pointer', border: 'none', background: 'transparent', color: 'var(--ds-link)' }}>unassign</button></span>
                  : <button type="button" disabled={busy} onClick={() => act(() => adminUpdate(open.id, { assign_to: 'me' }))} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--ds-border-strong)', background: 'var(--ds-surface)', cursor: 'pointer' }}>Assign to me</button>}
              </div>

              <TicketHistory ticket={open} viewer="admin" />

              <form style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
                    onSubmit={(e) => { e.preventDefault(); if (draft.trim()) act(() => adminReply(open.id, draft)); }}>
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} placeholder="Reply to the customer — they get an email and the ticket moves to Waiting on customer"
                          style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8, border: '1px solid var(--ds-border-strong)', font: 'inherit', fontSize: 13.5 }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={busy || !draft.trim()} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Send reply</button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default MessagesSection;
